<template>
  <div>
    <h4 class="tw-text-[#6d7287] tw-text-base tw-mb-5">
      {{
        isEditMode
          ? t('customer_management.projects.edit_project')
          : t('customer_management.projects.create_project')
      }}
    </h4>

    <Card class="tw-p-8">
      <div
        class="tw-text-base tw-font-medium tw-mb-4 tw-uppercase tw-text-[#3f4456] tw-pb-4 tw-border-b tw-border-[#d7ddf5]"
      >
        {{ t('customer_management.projects.create_project') }}
      </div>

      <form @submit.prevent="onSubmit" class="tw-flex-1 tw-w-full tw-px-4 tw-flex tw-flex-col">
        <!-- Project Name and Project Limit -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
          <div>
            <Label>{{ t('customer_management.forms.project_name') }}</Label>
            <Input
              v-model.trim="$v.name.$model"
              :class="{ 'tw-border-danger': $v.name.$error }"
              type="text"
              :placeholder="t('customer_management.forms.name_placeholder')"
            />
            <div v-if="$v.name.$error" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ t('customer_management.forms.project_name_required_error') }}
            </div>
          </div>
          <div>
            <Label>
              {{ t('customer_management.titles.set_project_limit') }}
            </Label>
            <CurrencyInput
              v-model.trim="$v.project_limit.$model"
              :errorClass="{
                'tw-border-danger': $v.project_limit.$error,
              }"
              :placeholder="t('customer_management.forms.limit_placeholder')"
            />
            <div v-if="$v.project_limit.$error" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ t('customer_management.forms.project_limit_required_error') }}
            </div>
          </div>
        </div>

        <!-- Auto Approval Limit and Frequency -->
        <div class="tw-grid tw-grid-cols-1 lg:tw-grid-cols-2 tw-gap-x-8 tw-gap-y-4 tw-mb-4">
          <div>
            <Label>
              {{ t('customer_management.forms.auto_approval_field_hint') }}
            </Label>
            <CurrencyInput
              v-model.trim="$v.auto_approval_limit.$model"
              :errorClass="{
                'tw-border-danger': $v.auto_approval_limit.$error,
              }"
              :placeholder="t('customer_management.forms.limit_placeholder')"
            />
            <div v-if="$v.auto_approval_limit.$error" class="tw-text-danger tw-text-xs tw-mt-1">
              {{ t('customer_management.assign_team.auto_approval_limit_required') }}
            </div>
          </div>
          <div>
            <Label>
              {{ t('customer_management.titles.project_limit_frequency') }}
            </Label>
            <Select v-model="formData.project_limit_frequency">
              <SelectTrigger>
                <SelectValue :placeholder="t('customer_management.main_data.frequency_select')" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="NONE">{{ t('common.data.none') }}</SelectItem>
                <SelectItem value="WEEKLY">
                  {{ t('customer_management.main_data.frequency_weekly') }}
                </SelectItem>
                <SelectItem value="MONTHLY">
                  {{ t('customer_management.main_data.frequency_monthly') }}
                </SelectItem>
                <SelectItem value="YEARLY">
                  {{ t('customer_management.main_data.frequency_yearly') }}
                </SelectItem>
                <SelectItem value="ALL_TIME">
                  {{ t('customer_management.main_data.frequency_all_time') }}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div v-if="isCreatedSuccessfully != null" class="tw-mb-4">
          <Alert v-if="isCreatedSuccessfully" class="lg:tw-w-7/12">
            <AlertDescription>
              {{
                isEditMode
                  ? t('customer_management.forms.project_update_success')
                  : t('customer_management.forms.project_create_success')
              }}
            </AlertDescription>
          </Alert>
          <Alert v-else variant="destructive" class="lg:tw-w-7/12">
            <AlertDescription>{{ errorMessage }}</AlertDescription>
          </Alert>
        </div>

        <div class="tw-flex tw-gap-2 tw-justify-end">
          <Button type="button" variant="secondary" @click="router.push({ name: 'projects' })">
            {{ t('common.forms.cancel_discard') }}
          </Button>
          <CustomerActionButton
            type="submit"
            class="tw-bg-primary-500 tw-text-white tw-px-4 tw-py-2 tw-rounded-md tw-text-sm"
          >
            {{
              isEditMode
                ? t('customer_management.projects.edit_project')
                : t('projects.common.add_button')
            }}
          </CustomerActionButton>
        </div>
      </form>
    </Card>
  </div>
</template>

<script setup lang="ts">
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import CurrencyInput from '@/components/shared/currency-input/CurrencyInput.vue';

import { ref, reactive, onMounted, type Ref } from 'vue';
import router from '@/router';
import { useRouter, useRoute } from 'vue-router';
import { useVuelidate } from '@vuelidate/core';
import { required } from '@vuelidate/validators';
import { useProjectStore } from '@/stores/project.store';

const route = useRoute();
const { createProject, getProjectById, updateProject } = useProjectStore();
import { useI18n } from 'vue-i18n';
import CustomerActionButton from '@/components/shared/CustomerActionButton.vue';
import { useBreadcrumbs } from '@/composables/useBreadcrumbs';
import cookieService from '@/_helpers/services/cookie.service';

const { t } = useI18n();
const { setBreadcrumbs } = useBreadcrumbs();

const formData: any = reactive({
  customer: '',
  name: '',
  project_limit: null,
  auto_approval_limit: null,
  project_limit_frequency: 'ALL_TIME',
});

const isCreatedSuccessfully: Ref<boolean | null> = ref(null);
const errorMessage: any = ref('');

let isEditMode: any = ref(false);

let projectIdEdit: any = '';

const rules = {
  customer: {
    required,
  },
  name: {
    required,
  },
  project_limit: {
    required,
  },
  auto_approval_limit: {
    required,
  },
};

const $v = useVuelidate(rules, formData);

const onSubmit = async () => {
  $v.value.$touch();
  if (!$v.value.$invalid) {
    try {
      if (!isEditMode.value) {
        await createProject(formData);
        resetProject();
      } else {
        await updateProject(formData, projectIdEdit);
      }
      isCreatedSuccessfully.value = true;

      if (isEditMode.value) {
        setTimeout(() => {
          router.push({ name: 'projects' });
        }, 1000);
      }
    } catch (error: any) {
      console.log(error);
      isCreatedSuccessfully.value = false;
      errorMessage.value = error.response.data.errors[0].detail;
    }
  }
};

const getCustomerProjectById = async () => {
  try {
    let data = await getProjectById(projectIdEdit);
    if (data.customer == formData.customer) {
      Object.keys(formData).forEach(formDataItem => {
        Object.keys(data).forEach(responseItem => {
          if (formDataItem == responseItem) {
            formData[formDataItem] = data[responseItem];
          }
        });
      });
    } else {
      router.push({ name: 'projects' });
    }
  } catch (e) {
    console.log(e);
  }
};

const resetProject = () => {
  formData.name = '';
  formData.project_limit = null;
  formData.auto_approval_limit = null;
  formData.project_limit_frequency = 'ALL_TIME';
  $v.value.$reset();
};

onMounted(async () => {
  isEditMode.value = !!route.params.id;
  projectIdEdit = route.params.id as string;

  setBreadcrumbs([
    {
      labelKey: 'sidebar.items.projects',
      to: { name: 'projects' },
    },
    {
      label: isEditMode.value ? t('common.forms.edit') : t('common.actions.create'),
    },
  ]);

  const selectedCustomerId = cookieService.get('selectedCustomerId');
  if (selectedCustomerId) {
    formData.customer = parseInt(selectedCustomerId);
  }

  if (isEditMode.value) {
    await getCustomerProjectById();
  }
});
</script>
